import isArchived from 'archive/isArchived';
import {GroupActionFunctionParameters} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import hasBlockingValidationErrors from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import tableHasUnsubmittedChanges from 'notebook/structured-tables/util/tableHasUnsubmittedChanges';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TableIndicatorStatus} from 'notebook/tables/TableConstants';
import {AnyStructuredTable} from 'notebook/util/NoteUtilConstants';

function containedTablesHaveValidationErrors(evaluatedTableMap: {
  [tableName: string]: Array<Array<EvaluatedCell>>;
}): boolean {
  return Object.values(evaluatedTableMap).some((computedTable) => hasBlockingValidationErrors(computedTable));
}

function containedTablesHaveUnsubmittedChanges(
  containedStructuredTables: Array<AnyStructuredTable>,
  evaluatedTableMap: {[tableName: string]: Array<Array<EvaluatedCell>>}
): boolean {
  const tablePairings = containedStructuredTables.map((structuredTable) => ({
    structuredTable,
    computedTable: evaluatedTableMap[structuredTable.name],
  }));
  return tablePairings.some(({structuredTable, computedTable}) =>
    tableHasUnsubmittedChanges(structuredTable, computedTable)
  );
}

export default function isGroupActionButtonDisabled<TGroupMetadata, TCreateGroupExtras>({
  entryOrTemplateId,
  evaluatedTableMap,
  noteLinkedGroup,
  noteLinkedGroupAdapter,
  locked,
}: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>): boolean {
  const containedStructuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  return (
    locked ||
    !isInExecutableCanvas({entryOrTemplateId, structuredTable: containedStructuredTables[0]}) ||
    containedTablesHaveValidationErrors(evaluatedTableMap) ||
    !containedTablesHaveUnsubmittedChanges(containedStructuredTables, evaluatedTableMap) ||
    noteLinkedGroupAdapter.getGroupSchemas(noteLinkedGroup).some((schema) => isArchived(schema))
  );
}

export function getGroupActionButtonTableStatus<TGroupMetadata, TCreateGroupExtras>({
  evaluatedTableMap,
  noteLinkedGroup,
  noteLinkedGroupAdapter,
}: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>): TableIndicatorStatus {
  const containedStructuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  if (containedTablesHaveValidationErrors(evaluatedTableMap)) {
    return TableIndicatorStatus.ERROR;
  } else if (containedTablesHaveUnsubmittedChanges(containedStructuredTables, evaluatedTableMap)) {
    return TableIndicatorStatus.UNSUBMITTED_CHANGES;
  } else if (noteLinkedGroupAdapter.getGroupSchemas(noteLinkedGroup).some((schema) => isArchived(schema))) {
    return TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED;
  }
  return TableIndicatorStatus.NO_STATUS;
}

export function getGroupActionButtonDisabledTooltip<TGroupMetadata, TCreateGroupExtras>({
  entryOrTemplateId,
  evaluatedTableMap,
  noteLinkedGroup,
  noteLinkedGroupAdapter,
  locked,
}: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>): string | null {
  const containedStructuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  if (locked) {
    return 'Entry is locked';
  } else if (!isInExecutableCanvas({entryOrTemplateId, structuredTable: containedStructuredTables[0]})) {
    return 'Submission not allowed in templates or config documents';
  } else if (containedTablesHaveValidationErrors(evaluatedTableMap)) {
    return 'Validation error(s) are present';
  } else if (!containedTablesHaveUnsubmittedChanges(containedStructuredTables, evaluatedTableMap)) {
    return 'All changes have been submitted';
  } else if (noteLinkedGroupAdapter.getGroupSchemas(noteLinkedGroup).some((schema) => isArchived(schema))) {
    return 'Submission not allowed for archived schema';
  } else {
    return null;
  }
}
